//#region redux
import { store } from "store";
import { useDispatch } from "react-redux";
import { resetFAQDiagnostics } from "store/actions/diagnosticsActions";
//#endregion
import { CSSHelper, EnumHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
//#region types
import { AuthEnums, StyleEnums } from "models/enums";
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { TranslationKeys } from "translations";
import { FaqDiagnostics } from "models/stateTypes/diagnosticsState";
import { CommonConstants, ServiceConstants, FAQConstants } from "utils/constants";
import { SupportFormTypes } from "models/formTypes";
import { DiagnosticsEnums } from "models/enums";
//#endregion
import useDiagnosticsService from "./useDiagnosticsService";
import useAPIClientHelper from "./useAPIClientHelper";
import cssExports from "theme/styles/main.module.scss";
import useLoggingService from "./useLoggingService";
import { SupportService } from "services";

function useSupportService() {
  const { apiClientInstance } = useAPIClientHelper();

  const dispatch = useDispatch();
  const diagnosticsService = useDiagnosticsService();
  const loggingService = useLoggingService();

  const getById = async (issueId: number) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company).SupportClient.GetAsync(issueId);
    return res.data;
  };

  const getSupportIssues = (filter: WintUtilsWebApi.Filter) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .SupportClient.Filter({
        ...filter,
        severities: [],
        isAutoPaginating: true,
      })
      .then((res) => res.data);
  };

  const addCommentAsync = (id: number, post: ApiModels.SupportCommentPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company).SupportClient.AddCommentAsync(id, post);
  };

  const getComments = (issueId: number) => {
    const filter: ApiModels.SupportCommentFilter = {
      ...ServiceConstants.DefaultFilter,
      IncludeSelf: false,
    } as ApiModels.SupportCommentFilter;
    return apiClientInstance(AuthEnums.AuthType.Company)
      .SupportClient.GetComments(issueId, filter)
      .then((res) => (res.data.items ? res.data.items : []));
  };

  const convertToSupportCommentPost = (comment: string, files?: UploadedFile[]) => {
    const supportCommentPost: ApiModels.SupportCommentPost = {
      attachments: files as ApiModels.SupportAttachment[],
      message: comment,
    };

    return supportCommentPost;
  };

  const getAttachment = async (issueId: number, simpleAttachmentId: number): Promise<string | undefined> => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .SupportClient.GetAttachmentAsync(issueId, simpleAttachmentId)
      .then((res) => res.data.data);
  };

  const downloadAttachment = (issueId: number, attachmentId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).SupportClient.DownloadAttachment(issueId, attachmentId);
  };

  const exportConversation = (issueId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).SupportClient.ExportConversationAsync(issueId);
  };

  const getSuggestedSupportPersons = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .SupportClient.GetSuggestedSupportPersons({
        count: ServiceConstants.SuggestionsCount,
      })
      .then((res) => (res.data.items ? res.data.items : []));
  };

  const ForwardSupportIssue = (id: number, employeeId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SupportClient.SendToAsync(id, employeeId);
  };

  // The byrå-forward target list is small and bounded (Wint + the byrå's own colleagues with an
  // active relation to the company) — mapped directly to SelectionItem rather than routed through
  // UtilsHelper.convertToResultItem, whose input type (SearchResult, id: string) doesn't structurally
  // match the client's User (id: number).
  const getAgencyForwardTargets = (id: number): Promise<SelectionItem[]> => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .SupportClient.GetAgencyForwardTargets(id)
      .then((res) =>
        (res.data ?? []).map(
          (user, index): SelectionItem => ({
            id: user.id,
            value: user.name ?? "",
            // Core puts Wint's support group first, named "Wint"; an avatar without id renders the Wint logo.
            avatar: index === 0 && user.name === CommonConstants.Wint ? {} : { id: user.id, name: user.name },
          })
        )
      );
  };

  // AgencyForwardAsync accepts either target.toWint or a personId matching Wint's own support-group
  // id as equivalent (see Core's AgencyForwardAsync) — Wint appears as an ordinary entry in
  // getAgencyForwardTargets' list, so the client never needs to special-case it and always posts back
  // whichever id was picked.
  const AgencyForwardSupportIssue = (id: number, personId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SupportClient.AgencyForwardAsync(id, { toWint: false, personId });
  };

  const markAsResolved = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SupportClient.ResolveAsync(id);
  };

  const confirm = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SupportClient.ConfirmAsync(id);
  };

  const createTicket = (issue: WintSuperkollAPIModel.SupportIssuePost, showErrors = true): Promise<number> => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors,
    })
      .SupportClient.PostAsync(issue)
      .then((res) => res.data);
  };

  const getStates = () => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.SupportIssueState).map((key) => {
      return {
        id: Number(key),
        value: SupportService.getStateText(key),
      };
    });
  };

  const getSupportCases = (supportIssues: ApiModels.SupportIssueListItem[]) => {
    return supportIssues.map((supportIssue) => supportIssue as unknown as ApiModels.SupportIssue);
  };

  const getStateLabelClass = (status: ApiModels.SupportIssueState) => {
    switch (status) {
      case ApiModels.SupportIssueState.AtSupport:
        return cssExports.blueTag;
      case ApiModels.SupportIssueState.AtCustomer:
        return cssExports.yellowTag;
      case ApiModels.SupportIssueState.Closed:
        return cssExports.greenTag;
      default:
        return cssExports.grayTag;
    }
  };

  // Byråsupport's label matrix on top of the ordinary state text/class above. An unstamped issue
  // (isLocked/isOwnedByAgency/isViewerAgencyMember all false) falls straight through every branch here
  // to the exact same result the plain functions already gave — this only adds behaviour, it never
  // changes it, for a case that isn't stamped for byråsupport.
  //
  // isLocked wins over everything: it implies State === Closed and isReadOnly on the server (a byrå
  // that left the company, or ran out of colleagues to hand the case to), and "Löst" would be
  // misleading — the case isn't answered, it's unanswerable. Below that, AtSupport owned by the
  // agency reads "Hos din byrå" for the customer and "Hos mig" for the byrå member looking at their
  // own open case; AtCustomer reads "Hos kunden" for the byrå member, unchanged "Hos mig" for the
  // customer. Wint-owned AtSupport and Closed-not-locked are unchanged in both directions.
  const getAgencyAwareStateText = (
    supportIssue: Pick<ApiModels.SupportIssue, "state" | "isLocked" | "isOwnedByAgency" | "isViewerAgencyMember">
  ) => {
    if (supportIssue.isLocked) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.State_StangtForSvar);
    }

    if (supportIssue.state === ApiModels.SupportIssueState.AtSupport && supportIssue.isOwnedByAgency) {
      return LocalizationHelperInstance.getFormattedMessage(
        supportIssue.isViewerAgencyMember
          ? TranslationKeys.SupportIssue.State_1
          : TranslationKeys.SupportIssue.State_HosDinByra
      );
    }

    if (supportIssue.state === ApiModels.SupportIssueState.AtCustomer && supportIssue.isViewerAgencyMember) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.State_HosKunden);
    }

    return SupportService.getStateText(supportIssue.state);
  };

  // Everyone in the conversation: the listed participants, plus Wint as the implied counterpart of every
  // case the byrå does not own. A byrå-owned case has no Wint in it, so the customer and one byrå member
  // are just the two sides talking, not "several participants".
  const getConversationParticipantCount = (
    supportIssue: Pick<ApiModels.SupportIssue, "participants" | "isOwnedByAgency">
  ) => (supportIssue.participants?.length ?? 0) + (supportIssue.isOwnedByAgency ? 0 : 1);

  // Yellow = the viewer is expected to answer, blue = someone else is, for every role.
  const getAgencyAwareStateDisplayType = (
    supportIssue: Pick<ApiModels.SupportIssue, "state" | "isLocked" | "isOwnedByAgency" | "isViewerAgencyMember">
  ) => {
    if (supportIssue.isLocked) {
      return StyleEnums.DisplayType.None;
    }

    switch (supportIssue.state) {
      case ApiModels.SupportIssueState.AtSupport:
        return supportIssue.isOwnedByAgency && supportIssue.isViewerAgencyMember
          ? StyleEnums.DisplayType.Warning
          : StyleEnums.DisplayType.Info;
      case ApiModels.SupportIssueState.AtCustomer:
        return supportIssue.isViewerAgencyMember ? StyleEnums.DisplayType.Info : StyleEnums.DisplayType.Warning;
      case ApiModels.SupportIssueState.Closed:
        return StyleEnums.DisplayType.Success;
      default:
        return StyleEnums.DisplayType.None;
    }
  };

  const getAgencyAwareStateLabelClass = (
    supportIssue: Pick<ApiModels.SupportIssue, "state" | "isLocked" | "isOwnedByAgency" | "isViewerAgencyMember">
  ) => CSSHelper.getTagClass(getAgencyAwareStateDisplayType(supportIssue));

  const filter = (filter: ApiModels.SupportIssueFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).SupportClient.Filter(filter);
  };

  const filterListitems = (filter: ApiModels.SupportIssueFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).SupportClient.ListItems(filter);
  };
  const SupportIssueListItemFilter = (baseFilter: WintUtilsWebApi.Filter, formFilter?: SupportFormTypes.Filter) => {
    return filterListitems({
      ...baseFilter,
      ...supportFilterToAPIFilter(formFilter),
    });
  };

  const SupportIssueFilter = (baseFilter: WintUtilsWebApi.Filter, formFilter?: SupportFormTypes.Filter) => {
    return filter({
      ...baseFilter,
      ...supportFilterToAPIFilter(formFilter),
    });
  };

  const supportFilterToAPIFilter = (
    formFilter?: SupportFormTypes.Filter
  ): Omit<ApiModels.SupportIssueFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      states: formFilter?.states?.map((s) => s.id as ApiModels.SupportIssueState) ?? [],
      severities: formFilter?.severities?.map((s) => s.id as ApiModels.SupportIssueSeverity) ?? [],
      userIds: formFilter?.userId ? [formFilter?.userId] : [],
      searchString: formFilter?.searchString,
      createdDateFrom: formFilter?.created?.start,
      createdDateTo: formFilter?.created?.end,
      lastUpdatedFrom: formFilter?.updated?.start,
      lastUpdatedTo: formFilter?.updated?.end,
    };
  };

  const getFAQSuggestions = (searchString: string, maxItems: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { timeout: FAQConstants.FAQServiceTimeountMs })
      .FAQServiceClient.GetFAQ(searchString, maxItems)
      .then((res) => res.data);
  };

  const submitCommentRating = (commentId: number, rating: WintSuperkollAPIModel.SupportCommentRatingPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SupportClient.AddCommentRatingAsync(commentId, rating);
  };

  const getUpdatedSupportIssueWithRatingFlag = (supportIssue: ApiModels.SupportIssue): ApiModels.SupportIssue => {
    if (supportIssue?.comments === undefined || supportIssue.comments.length === 0) {
      const err = new Error();
      Error.captureStackTrace(err);
      const message = `Unexpected error occured in SupportService.getUpdatedSupportIssueWithRatingFlag. No support issue comments found.`;

      loggingService.logError(message, err.stack ?? "No stack trace available");
      return supportIssue;
    }

    const targetSupportComment = supportIssue.comments[supportIssue?.comments.length - 1];
    const maskedSupportComments = supportIssue.comments?.filter((c) => c.id !== targetSupportComment?.id) ?? [];

    const updatedSupportComment = { ...targetSupportComment, hasRating: true };

    const supportComments = [...maskedSupportComments, updatedSupportComment];

    const updatedSupportIssue = {
      ...supportIssue,
      comments: supportComments,
    };

    return updatedSupportIssue;
  };

  /**
   * @deprecated This function is deprecated. Move to a custom hook and use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Create a new custom hook (e.g., useFAQSupportFlowDiagnostics)
   * 2. Use useSelector to access Redux state instead of store.getState()
   * 3. Move this logic into the custom hook
   * 4. Use the hook in components instead of calling this function
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const result = supportService.submitFAQSupportFlowDiagnostics();
   *
   * @example
   * // New way (recommended):
   * function useFAQSupportFlowDiagnostics() {
   *   const faqDiagnosticsState = useSelector((state: AppState) => state.diagnostics.faqDiagnostics);
   *
   *   const submitFAQSupportFlowDiagnostics = (exitActionOverride?: DiagnosticsEnums.ExitAction) => {
   *     // Move existing logic here using faqDiagnosticsState
   *   };
   *
   *   return { submitFAQSupportFlowDiagnostics };
   * }
   *
   * // In component:
   * const { submitFAQSupportFlowDiagnostics } = useFAQSupportFlowDiagnostics();
   * const result = submitFAQSupportFlowDiagnostics();
   */
  const submitFAQSupportFlowDiagnostics = (exitActionOverride?: DiagnosticsEnums.ExitAction) => {
    //TODO refactor to use useSelector instead - https://app.clickup.com/t/4523827/DEV-12514
    const faqDiagnosticsState = store.getState().diagnostics.faqDiagnostics;

    function translateFaqDiagnostics(faqDiagnostics: FaqDiagnostics): WintSuperkollAPIModel.DiagnosticsPost {
      const properties: { [index: string]: string } = {
        visitedDefaultFAQArticle: faqDiagnostics.visitedDefaultFAQArticle.toString(),
        sentSupportCase:
          exitActionOverride === DiagnosticsEnums.ExitAction.SentSupportCase
            ? "true"
            : JSON.stringify(faqDiagnostics.sentSupportCase),
        importantActions: JSON.stringify(faqDiagnostics.importantActions),
        questions: JSON.stringify(faqDiagnostics.questions),
        questionCount: faqDiagnostics.questions.length.toString(),
        openedFAQArticleCount: faqDiagnostics.questions
          .reduce(
            (acc, curr) => acc + curr.visitedFAQArticleIds.length,
            faqDiagnostics.visitedDefaultFAQArticle ? 1 : 0
          )
          .toString(),
        suggestedFAQArticleCount: faqDiagnostics.questions
          .reduce((acc, curr) => acc + curr.retrievedFAQArticleIds.length, 0)
          .toString(),
        exitAction: exitActionOverride?.toString() ?? faqDiagnostics.exitAction?.toString() ?? "undefined",
      };

      return {
        namespaceName: "Webapp.FAQDiagnostics",
        eventName: "DiagnosticDataSubmit",
        properties,
      };
    }

    const diagnosticsPost = translateFaqDiagnostics(faqDiagnosticsState);

    dispatch(resetFAQDiagnostics());

    return diagnosticsService.trackEvent(diagnosticsPost);
  };

  const isSuspectedAccidentalClose = (faqDiagnostics: FaqDiagnostics) => {
    const clickedCreateSupportCase = faqDiagnostics.importantActions.includes("CLICKED_CREATE_SUPPORT_CASE");
    const sentNoSupportCase = !faqDiagnostics.sentSupportCase;

    return clickedCreateSupportCase && sentNoSupportCase;
  };

  const supportFormHasContent = (values?: SupportFormTypes.Create) => {
    if (!values) return false;

    return (
      !UtilsHelper.isEmpty(values.subject) ||
      !UtilsHelper.isEmpty(values.message) ||
      !UtilsHelper.isEmpty(values.category) ||
      // In agency mode there is no category, so without these two a user who had only picked a
      // recipient would be treated as having an empty form and lose the choice without a warning.
      // Compared against undefined rather than via isEmpty-style truthiness because
      // SupportIssueRecipient.Agency is 0.
      values.recipient !== undefined ||
      (values.persons?.length ?? 0) > 0 ||
      (values.attachments?.length ?? 0) > 0
    );
  };

  return {
    getById,
    getSupportIssues,
    addCommentAsync,
    getComments,
    convertToSupportCommentPost,
    getAttachment,
    downloadAttachment,
    exportConversation,
    getSuggestedSupportPersons,
    ForwardSupportIssue,
    markAsResolved,
    confirm,
    createTicket,
    getStates,
    getSupportCases,
    getStateLabelClass,
    getAgencyAwareStateText,
    getConversationParticipantCount,
    getAgencyAwareStateLabelClass,
    getAgencyForwardTargets,
    AgencyForwardSupportIssue,
    filter,
    filterListitems,
    SupportIssueListItemFilter,
    SupportIssueFilter,
    supportFilterToAPIFilter,
    getFAQSuggestions,
    submitCommentRating,
    getUpdatedSupportIssueWithRatingFlag,
    submitFAQSupportFlowDiagnostics,
    isSuspectedAccidentalClose,
    supportFormHasContent,
  };
}

export default useSupportService;
